import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User, AuthState } from '@/types';
import { authService } from '@/services';
import { STORAGE_KEYS } from '@/constants';

interface AuthStore extends AuthState {
  // Actions
  justLoggedIn: boolean;
  resetEmail: string | null;
  setJustLoggedIn: (value: boolean) => void;
  setResetEmail: (email: string | null) => void;
  setUser: (user: User | null) => void;
  setLoading: (loading: boolean) => void;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  forceLogoutLocal: () => void;
}

let checkAuthInflight: Promise<void> | null = null;

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      // Initial state
      user: null,
      isAuthenticated: false,
      isLoading: false,
      justLoggedIn: false,
      resetEmail: null,

      // Actions
      setJustLoggedIn: (value: boolean) => {
        set({ justLoggedIn: value });
      },

      setResetEmail: (email: string | null) => {
        set({ resetEmail: email });
      },

      setUser: user => {
        set({ user, isAuthenticated: !!user });
      },

      setLoading: isLoading => {
        set({ isLoading });
      },

      login: async (username, password) => {
        set({ isLoading: true });
        try {
          const response = await authService.login({ username, password });
          set({
            user: response.user,
            isAuthenticated: true,
            isLoading: false,
            justLoggedIn: true,
          });
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      logout: async () => {
        set({ isLoading: true });
        try {
          await authService.logout();
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            justLoggedIn: false,
            resetEmail: null,
          });
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      checkAuth: async () => {
        if (checkAuthInflight) return checkAuthInflight;

        checkAuthInflight = (async () => {
          if (!authService.isAuthenticated()) {
            set({ user: null, isAuthenticated: false, isLoading: false, justLoggedIn: false });
            return;
          }

          const currentState = get();

          try {
            const user = await authService.getCurrentUser();

            if (!user || user.role === undefined || Number.isNaN(Number(user.role))) {
              get().forceLogoutLocal();
              return;
            }

            // Only update state if user data actually changed
            // This prevents unnecessary re-renders of all components
            const userChanged = JSON.stringify(currentState.user) !== JSON.stringify(user);

            if (userChanged || !currentState.isAuthenticated) {
              set({ user, isAuthenticated: true, isLoading: false });
            }
          } catch {
            set({ user: null, isAuthenticated: false, isLoading: false });
          }
        })().finally(() => {
          checkAuthInflight = null;
        });

        return checkAuthInflight;
      },

      forceLogoutLocal: () => {
        localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
        localStorage.removeItem(STORAGE_KEYS.USER_INFO);
        localStorage.removeItem(STORAGE_KEYS.MENU_TYPE);

        set({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          justLoggedIn: false,
        });
      },
    }),
    {
      name: 'auth-storage',
      partialize: state => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);

